/**
 * Command system entry point
 * Registers all built-in command handlers and exports public API
 */

import { registerCommands } from './registry';
import {
  addCommandHandler,
  editCommandHandler,
  saveCommandHandler,
  deleteCommandHandler,
  revertCommandHandler,
  exportCommandHandler,
  exportTimesheetCommandHandler,
  exportToPdfCommandHandler,
  importCommandHandler,
  importSubscriptionProofBankStatementCommandHandler,
  refreshCommandHandler,
  resetFiltersCommandHandler,
  defaultCommandHandler,
  openProjectBoardCommandHandler,
  openChildTaskDetailsCommandHandler,
  copyRowHandler,
  deleteEntitiesHandler,
  addRelationsCommandHandler,
  editRolesCommandHandler,
  addLoginCommandHandler,
  changeCompanyBankAccountCommandHandler,
  setFieldValueCommandHandler,
  changeEmployeeManagerCommandHandler,
  importBankStatementManagementCommandHandler,
  salaryCalculateCommandHandler,
  createTaskForTeamCommandHandler,
  createTasksAndWorkLogsCommandHandler,
  generateInvoicesCommandHandler,
  missingEmployeeRatesCommandHandler,
  changeTotalHandler,
  printFormHandler,
  printFormAndSendEmailHandler,
  generateAutoInvoiceCommandHandler,
  employeeReviewSubmitCommandHandler,
  employeeReviewApproveByManagerCommandHandler,
  toggleGridVisibilityFilterCommandHandler,
  employeeReviewParticipantFeedbackSubmitCommandHandler,
  implementWithAiCommandHandler,
  configureAiProviderCommandHandler,
  sendInvoiceToClientCommandHandler,
  sendSimpleOutboundCommandHandler,
  replyToFinanceEmailCommandHandler,
  addFinanceEmailSenderToSpamListCommandHandler,
  sendToWiseCommandHandler,
  unmarkWiseCommandHandler,
  deleteFinanceEmailCommandHandler,
  restoreFinanceEmailCommandHandler,
  markFinanceEmailSpamCommandHandler,
  unmarkFinanceEmailSpamCommandHandler,
} from './handlers';

// Register all built-in command handlers
registerCommands([
  // CRUD commands
  {
    alias: 'add',
    handler: addCommandHandler,
    description: 'Add a new row or navigate to add form',
    category: 'crud',
  },
  {
    alias: 'edit',
    handler: editCommandHandler,
    description: 'Edit existing rows',
    category: 'crud',
  },
  {
    alias: 'save',
    handler: saveCommandHandler,
    description: 'Save pending changes',
    requiresUnsavedChanges: true,
    category: 'crud',
  },
  {
    alias: 'delete',
    handler: deleteCommandHandler,
    description: 'Delete selected rows',
    requiresSelection: true,
    category: 'crud',
  },
  {
    alias: 'revert',
    handler: revertCommandHandler,
    description: 'Revert unsaved changes',
    requiresUnsavedChanges: true,
    category: 'crud',
  },

  // Export commands
  {
    alias: 'export',
    handler: exportCommandHandler,
    description: 'Export grid data to Excel',
    category: 'export',
  },
  {
    alias: 'exporttimesheet',
    handler: exportTimesheetCommandHandler,
    description: 'Export timesheet data to Excel',
    category: 'export',
  },
  {
    alias: 'exporttopdf',
    handler: exportToPdfCommandHandler,
    description: 'Export grid data to PDF',
    category: 'export',
  },
  {
    alias: 'import',
    handler: importCommandHandler,
    description: 'Import rows from Excel',
    category: 'import',
  },
  {
    alias: 'importbankstatement',
    handler: importSubscriptionProofBankStatementCommandHandler,
    description: 'Preview and import subscription payment proofs from a bank statement',
    requiresSelection: false,
    category: 'import',
  },

  // Navigation commands
  {
    alias: 'refresh',
    handler: refreshCommandHandler,
    description: 'Refresh grid data',
    category: 'navigation',
  },
  {
    alias: 'resetfilters',
    handler: resetFiltersCommandHandler,
    description: 'Reset all filters',
    category: 'navigation',
  },
  {
    alias: 'openprojectboard',
    handler: openProjectBoardCommandHandler,
    description: 'Open the selected project board',
    category: 'navigation',
  },
  {
    alias: 'openchildtaskdetails',
    handler: openChildTaskDetailsCommandHandler,
    description: 'Open details for the selected child task',
    requiresSelection: true,
    category: 'navigation',
  },

  // Batch delete command
  {
    alias: 'deleteentities',
    handler: deleteEntitiesHandler,
    description: 'Delete selected rows',
    requiresSelection: true,
    category: 'crud',
  },

  // Copy command
  {
    alias: 'copyrow',
    handler: copyRowHandler,
    description: 'Copy selected rows',
    requiresSelection: true,
    category: 'crud',
  },
  {
    alias: 'addrelations',
    handler: addRelationsCommandHandler,
    description: 'Add multiple related records',
    category: 'crud',
  },
  // User management commands
  {
    alias: 'editroles',
    handler: editRolesCommandHandler,
    requiresSelection: true,
    category: 'custom',
  },
  {
    alias: 'addlogin',
    handler: addLoginCommandHandler,
    requiresSelection: true,
    category: 'custom',
  },
  {
    alias: 'changecompanybankaccount',
    handler: changeCompanyBankAccountCommandHandler,
    description: 'Change bank account details of selected invoices',
    requiresSelection: true,
    category: 'crud',
  },
  {
    alias: 'setfieldvalue',
    handler: setFieldValueCommandHandler,
    requiresSelection: true,
    category: 'crud',
  },
  {
    alias: 'sendtowise',
    handler: sendToWiseCommandHandler,
    description: 'Create Wise transfers for the selected supplier invoices',
    requiresSelection: true,
    category: 'custom',
  },
  {
    alias: 'unmarkwise',
    handler: unmarkWiseCommandHandler,
    description: 'Remove the "sent to Wise" mark from the selected invoices',
    requiresSelection: true,
    category: 'custom',
  },
  {
    alias: 'changeemployeemanager',
    handler: changeEmployeeManagerCommandHandler,
    description: 'Assign or change the manager for selected employees',
    requiresSelection: true,
    category: 'custom',
  },
  {
    alias: 'importbankstatementmanagement',
    handler: importBankStatementManagementCommandHandler,
    description: 'Preview and import bank statement transactions',
    category: 'import',
  },
  {
    alias: 'zsitecommandsalarycalculate',
    handler: salaryCalculateCommandHandler,
    description: 'Add employees to salary calculation',
    category: 'crud',
  },
  {
    alias: 'createtaskforteam',
    handler: createTaskForTeamCommandHandler,
    description: 'Create a task for every member of the current project team',
    category: 'custom',
  },
  {
    alias: 'createtasksandworklogsforemployees',
    handler: createTasksAndWorkLogsCommandHandler,
    description: 'Create tasks and automatic work logs for selected employees',
    category: 'custom',
  },
  {
    // Legacy BO command type is intentionally named "GenerateInvoces".
    alias: 'zsitecommandgenerateinvoces',
    handler: generateInvoicesCommandHandler,
    description: 'Generate invoices for selected salary calculation lines',
    requiresSelection: false,
    category: 'custom',
  },
  {
    alias: 'missingemployeerates',
    handler: missingEmployeeRatesCommandHandler,
    description: 'Show employees with missing client rates',
    category: 'custom',
  },

  // Change total (timesheet)
  {
    alias: 'changetotal',
    handler: changeTotalHandler,
    description: 'Cycle timesheet view: All → by Client → by Project',
    category: 'crud',
  },

  // Print form command
  {
    alias: 'zprintform',
    handler: printFormHandler,
    description: 'Generate and download document from template for selected rows',
    requiresSelection: true,
    category: 'export',
  },

  // Payroll commands
  {
    alias: 'printformandsendemail',
    handler: printFormAndSendEmailHandler,
    description: 'Generate payroll documents and send via email',
    requiresSelection: true,
    category: 'custom',
  },

  // Generate auto invoice
  {
    alias: 'zsitecommandgenerateautoinvoice',
    handler: generateAutoInvoiceCommandHandler,
    description: 'Generate auto invoice from settings',
    category: 'custom',
  },
  {
    alias: 'employeereviewsubmit',
    handler: employeeReviewSubmitCommandHandler,
    category: 'custom',
  },
  {
    alias: 'employeereviewapprovebymanager',
    handler: employeeReviewApproveByManagerCommandHandler,
    category: 'custom',
  },
  {
    alias: 'togglegridvisibilityfilter',
    handler: toggleGridVisibilityFilterCommandHandler,
    category: 'custom',
  },
  {
    alias: 'employeereviewparticipantfeedbacksubmit',
    handler: employeeReviewParticipantFeedbackSubmitCommandHandler,
    category: 'custom',
  },
  {
    alias: 'implementwithai',
    handler: implementWithAiCommandHandler,
    description: 'Validate and implement the current task with AI',
    category: 'custom',
    availability: 'aiImplementation',
  },
  {
    alias: 'configureaiprovider',
    handler: configureAiProviderCommandHandler,
    description: 'Configure the current user AI provider and API key',
    category: 'custom',
  },
  {
    alias: 'sendinvoicetoclient',
    handler: sendInvoiceToClientCommandHandler,
    description: 'Send invoice to client',
    category: 'custom',
  },
  {
    alias: 'sendsimpleoutbound',
    handler: sendSimpleOutboundCommandHandler,
    description: 'Send a simple outbound email from client details',
    category: 'custom',
  },
  {
    alias: 'replytofinanceemail',
    handler: replyToFinanceEmailCommandHandler,
    description: 'Reply to an inbound finance email from email details',
    category: 'custom',
  },
  {
    alias: 'addfinancemailsendertospamlist',
    handler: addFinanceEmailSenderToSpamListCommandHandler,
    description: 'Add inbound sender email to the auto-spam list',
    category: 'custom',
  },
  {
    alias: 'deletefinanceemail',
    handler: deleteFinanceEmailCommandHandler,
    description: 'Soft-delete finance emails (archive)',
    category: 'custom',
  },
  {
    alias: 'restorefinanceemail',
    handler: restoreFinanceEmailCommandHandler,
    description: 'Restore soft-deleted finance emails',
    category: 'custom',
  },
  {
    alias: 'markfinanceemailspam',
    handler: markFinanceEmailSpamCommandHandler,
    description: 'Move finance emails to spam',
    category: 'custom',
  },
  {
    alias: 'unmarkfinanceemailspam',
    handler: unmarkFinanceEmailSpamCommandHandler,
    description: 'Remove finance emails from spam',
    category: 'custom',
  },
]);

// Export public API
export { commandRegistry, registerCommand, registerCommands, getCommandHandler } from './registry';
export { useDetailCommandAvailability } from './useDetailCommandAvailability';
export type {
  CommandContext,
  CommandResult,
  CommandHandler,
  CommandConfig,
  CommandExecutionOptions,
} from './types';
export { defaultCommandHandler };
